'use client'

import {
  type PolymorphicProps,
  type SlotComponentProps,
  useSlotProps,
} from '@mui/base'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type AccordionDetailsOwnProps<RootComponentType extends ElementType = 'div'> = {
  ref?: ComponentProps<RootComponentType>['ref']

  slotProps?: {
    root?: SlotComponentProps<
      RootComponentType,
      NonNullable<unknown>,
      undefined
    >
  }

  slots?: {
    root?: RootComponentType
  }
}

interface AccordionDetailsTypeMap<
  RootComponentType extends ElementType = 'div',
> {
  defaultComponent: RootComponentType
  props: AccordionDetailsOwnProps<RootComponentType>
}

export type AccordionDetailsProps<
  RootComponentType extends ElementType =
    AccordionDetailsTypeMap['defaultComponent'],
> = PolymorphicProps<
  AccordionDetailsTypeMap<RootComponentType>,
  RootComponentType
>

export const AccordionDetails = <RootComponentType extends ElementType = 'div'>(
  props: AccordionDetailsProps<RootComponentType>,
): JSX.Element => {
  const { ref, slotProps, slots, ...other } = props

  const Root = slots?.root ?? 'div'

  const rootProps = useSlotProps({
    additionalProps: {
      /**
       * Explicitly pass `ref` to `additionalProps` so `useSlotProps` resolves it,
       * if it's in `restProps`, `rootProps.ref` will be resolved to `null`.
       */
      ref,
    },
    className: 'accordion-details',
    elementType: Root,
    externalForwardedProps: other,
    externalSlotProps: slotProps?.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}
